"use client";

import { apiGet, apiPost, ApiError } from "@/lib/api-client";
import { clearSessionMarker, hasSessionMarker, setSessionMarker } from "./auth-marker";
import { getStoredAccessToken, setStoredAccessToken } from "@/lib/auth-token";

const AUTH_STORAGE_KEY = "app-auth";
const AUTH_CHANGED_EVENT = "auth:changed";

export type AuthUser = {
  id: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  email_verified: number;
  is_active: number;
  ecosystem_id: string | null;
  role: string;
};

type AuthResponse = {
  access_token: string;
  token_type: string;
  user: AuthUser;
};

type GoogleConfigResponse = {
  enabled: boolean;
  client_id: string | null;
};

type StoredAuthPayload = {
  user: AuthUser;
};

function emitAuthChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

function persistAuth(response: AuthResponse) {
  if (typeof window === "undefined") return response;
  setSessionMarker();
  setStoredAccessToken(response.access_token);
  try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: response.user } satisfies StoredAuthPayload)); } catch { /* Cookie session remains available. */ }
  emitAuthChanged();
  return response;
}

export function clearStoredAuth() {
  if (typeof window === "undefined") return;
  clearSessionMarker();
  setStoredAccessToken(null);
  try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch { /* Storage may be unavailable. */ }
  emitAuthChanged();
}

export function getStoredAuthUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuthPayload;
    return parsed?.user ?? null;
  } catch {
    return null;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export async function loginWithEmail(input: {
  email: string;
  password: string;
}) {
  const response = await apiPost<AuthResponse>("/auth/token", {
    grant_type: "password",
    email: input.email.trim().toLowerCase(),
    password: input.password,
  });
  return persistAuth(response);
}

export async function signupWithEmail(input: {
  email: string;
  password: string;
  fullName?: string;
  phone?: string;
  role?: "customer" | "komisyoncu";
}) {
  const response = await apiPost<AuthResponse>("/auth/signup", {
    email: input.email.trim().toLowerCase(),
    password: input.password,
    full_name: input.fullName?.trim() || undefined,
    phone: input.phone?.trim() || undefined,
    role: input.role ?? "customer",
    rules_accepted: true,
  });
  return persistAuth(response);
}

export async function loginWithGoogle(idToken: string) {
  const response = await apiPost<AuthResponse>("/auth/google", {
    id_token: idToken,
  });
  return persistAuth(response);
}

export async function fetchGoogleAuthConfig() {
  return apiGet<GoogleConfigResponse>("/auth/google/config");
}

/** Redirect-login sonrasi: httpOnly cookie oturumunu okuyup localStorage'a tasir. */
export async function bootstrapGoogleSession() {
  const response = await apiGet<AuthResponse>("/auth/session/bootstrap");
  return persistAuth(response);
}

export async function fetchCurrentUser() {
  const response = await apiGet<{ user: AuthUser }>("/auth/user");
  const currentToken = getStoredAccessToken();
  if (currentToken && response.user) {
    persistAuth({
      access_token: currentToken,
      token_type: "bearer",
      user: response.user,
    });
  }
  return response.user;
}

export async function rehydrateAuthSession() {
  try {
    if (!getStoredAccessToken()) {
      // Bu tarayicida hic oturum kurulmamissa bootstrap denenmez: cevap kesin
      // 401 olur, api-client bunu bir de token/refresh ile tekrarlar ve her
      // anonim ziyaret iki bosa istek + iki konsol hatasi uretir.
      if (!hasSessionMarker()) return null;
      const session = await bootstrapGoogleSession();
      return session.user;
    }
    return await fetchCurrentUser();
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      clearStoredAuth();
      return null;
    }
    // A temporary network/server failure must not erase a returning user's session.
    return getStoredAuthUser();
  }
}

export async function logout() {
  try {
    await apiPost<void>("/auth/logout");
  } finally {
    clearStoredAuth();
  }
}

export { AUTH_CHANGED_EVENT };

/** Sifre sifirlama baglantisi ister; hesap olsun olmasin ayni yanit doner. */
export async function requestPasswordReset(email: string) {
  return apiPost<{ success: boolean; message?: string }>("/auth/password-reset/request", {
    email: email.trim().toLowerCase(),
  });
}

/** E-postadaki token ile yeni sifreyi kaydeder. */
export async function confirmPasswordReset(token: string, password: string) {
  return apiPost<{ success: boolean; message?: string }>("/auth/password-reset/confirm", { token, password });
}
